import { useCallback, useEffect, useRef, useState } from "react";
import {
  capturePrivateSession,
  isPrivateSessionCurrent,
  subscribePrivateSession,
} from "@/lib/api/private-session";
import { parseRestaurantNotificationData } from "./notificationBehavior";
import { RESTAURANT_NOTIFICATION_TYPES } from "@/lib/firebase/notification-types";
import type { SoundControls, SoundStatus } from "./types/notificationSound";

const PREFERENCE_KEY = "restaurant.notification-sound";
const REMINDER_MS = 120_000;
function audioConstructor() {
  return window.AudioContext ?? (
    window as typeof window & { webkitAudioContext?: typeof AudioContext }
  ).webkitAudioContext;
}
function initiallyEnabled() {
  try {
    return localStorage.getItem(PREFERENCE_KEY) !== "muted";
  } catch {
    return true;
  }
}
function savePreference(enabled: boolean) {
  try {
    localStorage.setItem(PREFERENCE_KEY, enabled ? "enabled" : "muted");
  } catch {
    // Storage is optional; the in-memory preference still works.
  }
}

/** A device preference; audio and event deduplication belong to one private identity lifetime. */
export function useNotificationSound({ sellerId, storeId }: {
  sellerId: string | undefined;
  storeId: string | undefined;
}) {
  const [status, setStatus] = useState<SoundStatus>(() =>
    audioConstructor() ? initiallyEnabled() ? "locked" : "muted" : "unsupported",
  );
  const controls = useRef<SoundControls | null>(null);
  useEffect(() => {
    if (!sellerId || !storeId) return;
    const lifetime = capturePrivateSession();
    let active = true;
    let enabled = initiallyEnabled();
    let context: AudioContext | null = null;
    let lastAlertAt = -Infinity;
    const seen = new Set<string>();
    const tones = new Set<OscillatorNode>();
    const reminders = new Set<number>();
    const current = () => active && isPrivateSessionCurrent(lifetime);
    const stopTones = () => {
      for (const tone of tones) {
        try {
          tone.stop();
        } catch {
          // An already-ended tone needs no further stop.
        }
        tone.disconnect();
      }
      tones.clear();
    };
    const close = () => {
      reminders.forEach((timer) => window.clearInterval(timer));
      reminders.clear();
      stopTones();
      if (context) {
        context.onstatechange = null;
        void context.close().catch(() => undefined);
        context = null;
      }
    };
    const updateStatus = () => {
      if (!current()) return;
      setStatus(
        !audioConstructor() ? "unsupported"
          : !enabled ? "muted"
            : context?.state === "running" ? "ready" : "locked",
      );
    };
    const play = () => {
      if (!current() || !enabled || context?.state !== "running" || document.visibilityState === "hidden") return;
      try {
        const now = context.currentTime;
        for (const [offset, frequency] of [[0, 660], [0.4, 880], [0.8, 1174]]) {
          const tone = context.createOscillator();
          const gain = context.createGain();
          tone.type = "triangle";
          tone.frequency.setValueAtTime(frequency, now + offset);
          gain.gain.setValueAtTime(0.001, now + offset);
          gain.gain.linearRampToValueAtTime(0.65, now + offset + 0.015);
          gain.gain.exponentialRampToValueAtTime(0.001, now + offset + 0.36);
          tone.connect(gain);
          gain.connect(context.destination);
          tones.add(tone);
          tone.onended = () => {
            tones.delete(tone);
            tone.disconnect();
            gain.disconnect();
          };
          tone.start(now + offset);
          tone.stop(now + offset + 0.4);
        }
      } catch {
        updateStatus();
      }
    };
    const alert = () => {
      if (!current() || !enabled || context?.state !== "running" || document.visibilityState === "hidden") return;
      // Coalesce simultaneous alerts and reminders rather than queueing sound.
      const now = Date.now();
      if (now - lastAlertAt < 1200) return;
      lastAlertAt = now;
      play();
    };
    const unlock = async (sample: boolean) => {
      if (!current() || !enabled || !audioConstructor()) return;
      try {
        if (!context || context.state === "closed") {
          const Audio = audioConstructor()!;
          context = new Audio();
          context.onstatechange = updateStatus;
        }
        // Called only by a user gesture. No queued alert is replayed after unlock.
        await context.resume();
        if (!current()) return;
        updateStatus();
        if (sample) play();
      } catch {
        updateStatus();
      }
    };
    const owner: SoundControls = {
      enable: async () => {
        if (!current()) return;
        enabled = true;
        savePreference(true);
        await unlock(true);
      },
      mute: () => {
        if (!current()) return;
        enabled = false;
        savePreference(false);
        stopTones();
        updateStatus();
      },
      notify: (data) => {
        const parsed = parseRestaurantNotificationData(data);
        if (!current() || !parsed || parsed.storeId !== storeId) return;
        // D151: the nightly number is a report, not a call to the till — it
        // lands as the last drawer is counted, and must not ring the room.
        if (parsed.type === RESTAURANT_NOTIFICATION_TYPES.dayClosed) return;
        // D156: the approver's notice is for one person's phone, not the room.
        if (parsed.type === RESTAURANT_NOTIFICATION_TYPES.approvalUsed) return;
        // A storefront request has no bill yet: its own id is the event, and
        // the feed row and the push carry the same one, so they ring once.
        const eventId = data.commandId || data.paymentId || data.onlineOrderId;
        if (eventId) {
          const target = "invoiceId" in parsed ? parsed.invoiceId : parsed.storeId;
          const key = `${parsed.type}:${target}:${eventId}`;
          if (seen.has(key)) return;
          seen.add(key);
          if (seen.size > 256) seen.delete(seen.values().next().value!);
        }
        alert();
      },
      chime: (key) => {
        if (!current()) return;
        if (seen.has(key)) return;
        seen.add(key);
        if (seen.size > 256) seen.delete(seen.values().next().value!);
        alert();
      },
      startReminder: () => {
        if (!current()) return () => undefined;
        const timer = window.setInterval(alert, REMINDER_MS);
        reminders.add(timer);
        return () => {
          window.clearInterval(timer);
          reminders.delete(timer);
        };
      },
    };
    controls.current = owner;
    updateStatus();
    const gesture = (event: Event) => {
      // The explicit toggle owns its gesture. Resuming on pointerdown/keydown
      // here could turn Enable into Mute before that same gesture clicks it.
      if (event.target instanceof Element && event.target.closest("[data-restaurant-notification-sound-control]")) return;
      if (event.isTrusted && enabled && context?.state !== "running") void unlock(false);
    };
    document.addEventListener("pointerdown", gesture, true);
    document.addEventListener("click", gesture, true);
    document.addEventListener("keydown", gesture, true);
    const release = subscribePrivateSession(() => {
      if (!current()) close();
    });
    return () => {
      active = false;
      release();
      document.removeEventListener("pointerdown", gesture, true);
      document.removeEventListener("click", gesture, true);
      document.removeEventListener("keydown", gesture, true);
      if (controls.current === owner) controls.current = null;
      close();
    };
  }, [sellerId, storeId]);
  const enableSound = useCallback(() => controls.current?.enable(), []);
  const muteSound = useCallback(() => controls.current?.mute(), []);
  const notifySound = useCallback((data: Record<string, string>) => controls.current?.notify(data), []);
  const chimeSound = useCallback((key: string) => controls.current?.chime(key), []);
  const startSoundReminder = useCallback(() => controls.current?.startReminder(), []);
  return { soundStatus: status, enableSound, muteSound, notifySound, chimeSound, startSoundReminder };
}
